import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, MessageCircle, ShieldCheck, Lock, Trash2, EyeOff, Info } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import { getCurrentLocation } from "../lib/util";
import { IndoBlitzFooter } from "../components/IndoBlitz";
import AaryaMark from "../components/AaryaMark";
import HeroCarousel from "../components/HeroCarousel";
import TermsSheet from "../components/TermsSheet";
import TrustSheet from "../components/TrustSheet";

const PHONE_HINT = "+919876543210";

// Small reusable "why we ask" tooltip that opens on tap and closes on outside tap.
function WhyTip({ id, open, setOpen, children }) {
  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onClick={() => setOpen(open === id ? null : id)}
        data-testid={`why-tip-${id}`}
        className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-sand-100 text-ink-500 border border-sand-200 active:bg-sand-200"
        aria-label="Why we ask"
      >
        <Info size={11}/>
      </button>
      {open === id && (
        <span className="absolute left-6 -top-1 z-20 w-[240px] rounded-2xl bg-ink-900 text-white text-[12px] px-3 py-2 leading-snug shadow-lifted">
          {children}
        </span>
      )}
    </span>
  );
}

export default function Onboarding() {
  const { requestOtp, verifyOtp } = useAuth();
  const [step, setStep] = useState("welcome");
  const [mode, setMode] = useState("signup"); // "signup" (full onboarding) or "signin" (phone-only for returning users)
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [tc, setTc] = useState(false);
  const [otp, setOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [emailVerifiedToken, setEmailVerifiedToken] = useState("");
  // Persistent "you've signed in here before" flag lets us show a warm nudge
  // to returning users who have been signed out (cache clear, cookie expiry,
  // explicit logout) - so they never feel like a stranger to Aarya.
  const [isReturning, setIsReturning] = useState(false);
  useEffect(() => {
    try { setIsReturning(localStorage.getItem("aarya_prev_user") === "1"); } catch {}
  }, []);
  const [emailHint, setEmailHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [hint, setHint] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);
  const [trustOpen, setTrustOpen] = useState(false);
  const [openTip, setOpenTip] = useState(null);
  const [sentChannel, setSentChannel] = useState("whatsapp");  // tracks last delivery channel
  const [smsCountdown, setSmsCountdown] = useState(0);          // 30s -> 0; when 0, show "Send via SMS"
  const [smsResending, setSmsResending] = useState(false);
  // OTP resend cooldown: 30s countdown after each send, then the Resend button
  // lights up. Prevents users hammering the endpoint (and burning our SMS
  // budget) while still giving them a clear way to trigger a retry.
  const [otpCountdown, setOtpCountdown] = useState(0);
  useEffect(() => {
    if (step !== "otp" || otpCountdown <= 0) return;
    const t = setTimeout(() => setOtpCountdown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [step, otpCountdown]);
  // Kick the countdown any time we (re)send an OTP.
  useEffect(() => {
    if (step === "otp") setOtpCountdown(30);
  }, [step, sentChannel]);

  // Countdown timer: only relevant if we ever send WhatsApp first (Phase 2 - WABA setup required)
  useEffect(() => {
    if (step !== "otp" || sentChannel !== "whatsapp") return;
    setSmsCountdown(30);
    const t = setInterval(() => {
      setSmsCountdown((s) => {
        if (s <= 1) { clearInterval(t); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [step, sentChannel]);

  const onSubmitDetails = async () => {
    setErr("");
    if (!phone.startsWith("+")) { setErr("Phone must start with country code, e.g. +919876543210"); return; }
    if (!name.trim()) { setErr("Please tell us your name."); return; }
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) { setErr("Please enter a valid email."); return; }
    if (!tc) { setErr("Please accept the IndoBlitz Terms to continue."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-email-otp", { email: email.trim().toLowerCase(), name: name.trim() });
      setEmailHint(data?.hint || "");
      setStep("emailOtp");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || "Could not send the email code. Please try again.");
    } finally { setBusy(false); }
  };

  const onVerifyEmailOtp = async () => {
    setErr("");
    if (!emailOtp || emailOtp.length !== 6) { setErr("Please enter the 6-digit code from your email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email-otp", {
        email: email.trim().toLowerCase(),
        code: emailOtp.trim(),
      });
      if (!data?.email_verified_token) throw new Error("no token");
      setEmailVerifiedToken(data.email_verified_token);
      // Now trigger phone OTP
      const r = await requestOtp(phone.trim(), "sms");
      setHint(r.hint || "");
      setSentChannel(r.channel || "sms");
      setStep("otp");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || "Email code was not accepted. Please try again.");
    } finally { setBusy(false); }
  };

  const onResendEmailOtp = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-email-otp", { email: email.trim().toLowerCase(), name: name.trim() });
      setEmailHint(data?.hint || "");
      setEmailOtp("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || "Could not resend email code.");
    } finally { setBusy(false); }
  };

  const onSendOtp = onSubmitDetails; // kept for reference; single entry point for the submit button

  const onResendViaSms = async () => {
    setErr("");
    setSmsResending(true);
    try {
      const r = await requestOtp(phone.trim(), "sms");
      setHint(r.hint || "");
      setSentChannel(r.channel || "sms");
      setOtp("");
      // Explicit reset - the [step, sentChannel] effect no-ops when SMS -> SMS
      // (React's Object.is skips the effect), so we set the countdown here too.
      setOtpCountdown(30);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail) setErr(detail);
      else setErr("Could not resend via SMS. Please try again.");
    } finally { setSmsResending(false); }
  };

  const onVerify = async () => {
    setErr("");
    setBusy(true);
    try {
      const loc = await getCurrentLocation(2000);
      // Sign-in mode = returning user, backend picks them up by phone alone.
      // Sign-up mode = full first-time flow with name + email + email-verified-token.
      const extra = mode === "signin"
        ? { lat: loc?.lat, lng: loc?.lng }
        : { name: name.trim(), email: email.trim(), lat: loc?.lat, lng: loc?.lng, tc_accepted: true, email_verified_token: emailVerifiedToken };
      await verifyOtp(phone.trim(), otp.trim(), extra);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (detail) setErr(detail);
      else if (status) setErr(`Server returned ${status}. Please try again.`);
      else setErr("Network error. Please check your connection and try again.");
      // eslint-disable-next-line no-console
      console.error("[Aarya] Verify OTP failed:", e);
    } finally { setBusy(false); }
  };

  // Sign-in flow entry - existing user just needs their phone number.
  const startSignIn = async () => {
    setErr("");
    setMode("signin");
    if (!phone || !phone.startsWith("+")) {
      // Move to a phone-only step; user enters phone there.
      setStep("signinPhone");
      return;
    }
    setBusy(true);
    try {
      const r = await requestOtp(phone.trim(), "sms");
      setHint(r.hint || "");
      setSentChannel(r.channel || "sms");
      setStep("otp");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || "Could not send OTP. Please try again.");
    } finally { setBusy(false); }
  };

  const submitSignInPhone = async () => {
    setErr("");
    if (!phone.startsWith("+")) { setErr("Phone must start with country code, e.g. +919876543210"); return; }
    setBusy(true);
    try {
      const r = await requestOtp(phone.trim(), "sms");
      setHint(r.hint || "");
      setSentChannel(r.channel || "sms");
      setStep("otp");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || "Could not send OTP. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-6 pt-4 pb-6 min-h-[calc(100dvh-40px)] flex flex-col" data-testid="onboarding-screen">
      <AnimatePresence mode="wait">
        {step === "welcome" && (
          <motion.div key="welcome" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="flex-1 flex flex-col -mx-6 -mt-4">
            <HeroCarousel/>

            <div className="bg-sand-50 px-6 pt-2 flex-1 flex flex-col">
              <p className="font-display text-[34px] leading-[1.05] text-ink-900 tracking-tight mt-2">
                When you can&apos;t <span className="ink-underline">be there</span>,<br/>Aarya can.
              </p>
              <p className="mt-4 text-[14.5px] text-ink-700 leading-relaxed">
                One tap to say <span className="font-semibold text-sage-700">I am safe.</span>
                {" "}One long press to silently say <span className="font-semibold text-brick-500">I need help.</span> Your trusted circle knows in seconds, without you typing, opening a chat, or finding a contact.
              </p>

              <div className="mt-6 rounded-3xl border border-sand-200 bg-white overflow-hidden" data-testid="vs-whatsapp">
                <div className="px-5 pt-4 pb-2">
                  <p className="text-[11px] tracking-[0.18em] uppercase text-ink-500 font-semibold">Why not just WhatsApp?</p>
                </div>
                <div className="divide-y divide-sand-200">
                  {[
                    { wa: "Type, pick a chat, attach location, send", aa: "One tap. Done." },
                    { wa: "An attacker sees you typing", aa: "Silent SOS. No notification. No screen." },
                    { wa: "Parents may not know how to use it", aa: "A missed call works (coming soon)." },
                    { wa: "Nobody pings to check you’re okay", aa: "Aarya does. Daily." },
                  ].map((r, i) => (
                    <div key={i} className="grid grid-cols-2">
                      <div className="px-4 py-3 bg-sand-100/60">
                        <div className="flex items-center gap-1.5">
                          <MessageCircle size={11} className="text-ink-300 flex-shrink-0"/>
                          <span className="text-[10px] tracking-[0.14em] uppercase text-ink-500 font-semibold">WhatsApp</span>
                        </div>
                        <p className="text-[12.5px] text-ink-700 mt-1 leading-snug" dangerouslySetInnerHTML={{__html: r.wa}}/>
                      </div>
                      <div className="px-4 py-3 border-l border-sand-200">
                        <div className="flex items-center gap-1.5">
                          <ShieldCheck size={11} className="text-sage-600 flex-shrink-0"/>
                          <span className="text-[10px] tracking-[0.14em] uppercase text-sage-700 font-semibold">Aarya</span>
                        </div>
                        <p className="text-[12.5px] text-ink-900 font-medium mt-1 leading-snug">{r.aa}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="mt-5 text-[12px] text-ink-500 leading-relaxed">
                Basic features are <span className="font-semibold text-sage-700">free forever.</span> Premium add-ons for everyone, everywhere. Essential safety is never behind a paywall.
              </p>

              <div className="mt-5 rounded-3xl border border-sand-200 bg-white overflow-hidden" data-testid="trust-ribbon-welcome">
                <div className="px-5 py-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Lock size={16} className="text-sage-700 flex-shrink-0 mt-0.5"/>
                    <p className="text-[12.5px] text-ink-700 leading-snug"><span className="font-semibold text-ink-900">Encrypted end-to-end of the wire.</span> Your data is protected the moment it leaves your phone.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <EyeOff size={16} className="text-terracotta-600 flex-shrink-0 mt-0.5"/>
                    <p className="text-[12.5px] text-ink-700 leading-snug"><span className="font-semibold text-ink-900">Never sold. Never advertised.</span> No trackers, no third-party analytics, no background location.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Trash2 size={16} className="text-brick-500 flex-shrink-0 mt-0.5"/>
                    <p className="text-[12.5px] text-ink-700 leading-snug"><span className="font-semibold text-ink-900">Leave in one tap.</span> Delete your account and every trace of your data instantly - no forms, no delay.</p>
                  </div>
                </div>
                <button onClick={() => setTrustOpen(true)} data-testid="open-trust"
                  className="w-full py-3 border-t border-sand-200 text-sage-700 text-[13px] font-semibold active:bg-sand-100">
                  Read our full trust promise →
                </button>
              </div>

              <div className="mt-auto pt-8 pb-4">
                {isReturning && (
                  <div className="mb-4 rounded-2xl bg-sage-50 border border-sage-400/30 p-4" data-testid="welcome-back-banner">
                    <p className="text-[12px] tracking-[0.16em] uppercase text-sage-700 font-semibold">Welcome back</p>
                    <p className="text-[13px] text-ink-800 leading-snug mt-1">
                      Your Trust Circle needs you online for the protective net to be active. Sign in below.
                    </p>
                  </div>
                )}
                <button
                  onClick={() => { setMode("signup"); setStep("phone"); }}
                  data-testid="onboarding-get-started"
                  className="w-full h-[62px] rounded-full bg-ink-900 text-white text-base font-semibold tracking-wide flex items-center justify-center gap-2 active:scale-[0.99] transition shadow-lifted"
                >
                  {isReturning ? "Create another account" : "Get Started"} <ArrowRight size={18} />
                </button>
                <button
                  onClick={startSignIn}
                  data-testid="onboarding-sign-in"
                  className="mt-3 w-full h-[50px] rounded-full bg-white border border-ink-900/20 text-ink-900 text-[15px] font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition"
                >
                  Sign in <ArrowRight size={16} />
                </button>
                <p className="text-[11px] text-ink-500 text-center mt-2">
                  Already have an Aarya account? Sign in with your phone - no email needed.
                </p>
                <IndoBlitzFooter className="mt-5"/>
              </div>
            </div>
          </motion.div>
        )}

        {step === "phone" && (
          <motion.div key="phone" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1 flex flex-col">
            <button onClick={() => setStep("welcome")} className="text-ink-500 text-sm mb-4 self-start" data-testid="phone-back">← Back</button>
            <p className="font-display text-3xl text-ink-900 leading-tight">Tell us about you</p>
            <p className="text-sm text-ink-500 mt-2">Three details. Once. Then you&apos;re in.</p>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-[12px] tracking-wide uppercase text-ink-500 flex items-center gap-2">
                  Your name
                  <WhyTip id="name" open={openTip} setOpen={setOpenTip}>
                    Shown only inside the messages your family receives - so they know who tapped &quot;I am Safe&quot;.
                  </WhyTip>
                </label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Anjali Sharma"
                  data-testid="name-input"
                  className="mt-1 w-full h-[56px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-base outline-none focus:border-sage-500"/>
              </div>
              <div>
                <label className="text-[12px] tracking-wide uppercase text-ink-500 flex items-center gap-2">
                  Email
                  <WhyTip id="email" open={openTip} setOpen={setOpenTip}>
                    Verified with a one-time code. Used only if you ever lose your phone - never for marketing.
                  </WhyTip>
                </label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" type="email" inputMode="email"
                  data-testid="email-input"
                  className="mt-1 w-full h-[56px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-base outline-none focus:border-sage-500"/>
              </div>
              <div>
                <label className="text-[12px] tracking-wide uppercase text-ink-500 flex items-center gap-2">
                  Mobile number (with country code)
                  <WhyTip id="phone" open={openTip} setOpen={setOpenTip}>
                    Your login. So your family can reach you. Never shared with anyone else, ever.
                  </WhyTip>
                </label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={PHONE_HINT} inputMode="tel"
                  data-testid="phone-input"
                  className="mt-1 w-full h-[60px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-lg tracking-wide outline-none focus:border-sage-500"/>
                <p className="text-[11px] text-ink-500 mt-1">e.g. +91 for India · +1 for USA · +61 for Australia</p>
              </div>

              {/* TC checkbox row - large tap target */}
              <div
                onClick={() => setTc(!tc)}
                role="checkbox"
                aria-checked={tc}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setTc(!tc); } }}
                data-testid="tc-checkbox"
                className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white border-2 border-sand-200 active:bg-sand-100 text-left cursor-pointer select-none"
              >
                <span className={`h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0 border-2 ${tc ? "bg-sage-500 border-sage-500" : "bg-white border-sand-200"}`}>
                  {tc && <Check size={16} className="text-white" strokeWidth={3}/>}
                </span>
                <span className="text-[14px] text-ink-700 leading-snug">
                  I accept the IndoBlitz Terms &amp; Conditions and understand Aarya is not a replacement for emergency services.
                </span>
              </div>

              {/* Separate, large "Read Terms & Conditions" button - own tap target, opens the sheet */}
              <button
                type="button"
                onClick={() => setTermsOpen(true)}
                data-testid="open-terms"
                className="w-full mt-3 h-[52px] rounded-2xl bg-sand-100 border-2 border-sand-200 text-sage-700 font-semibold text-[14px] flex items-center justify-center gap-2 active:bg-sand-200 transition"
              >
                📖 Read the IndoBlitz Terms &amp; Conditions
              </button>
              <button
                type="button"
                onClick={() => setTrustOpen(true)}
                data-testid="open-trust-form"
                className="w-full mt-2 h-[44px] rounded-2xl bg-transparent text-ink-700 text-[12.5px] font-medium flex items-center justify-center gap-2"
              >
                <Lock size={14}/> Read the Aarya trust promise
              </button>
            </div>

            {err && <p className="mt-4 text-sm text-brick-500" data-testid="phone-error">{err}</p>}

            <div className="mt-auto pt-6">
              <button onClick={onSendOtp} disabled={busy} data-testid="send-otp-button"
                className="w-full h-[60px] rounded-full bg-sage-500 hover:bg-sage-600 text-white text-base font-semibold disabled:opacity-50 transition">
                {busy ? "Sending…" : "Continue → verify email"}
              </button>
              <IndoBlitzFooter/>
            </div>
          </motion.div>
        )}

        {step === "signinPhone" && (
          <motion.div key="signinPhone" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1 flex flex-col" data-testid="signin-phone-step">
            <button onClick={() => { setStep("welcome"); setMode("signup"); }} className="text-ink-500 text-sm mb-4 self-start" data-testid="signin-back">← Back</button>
            <p className="font-display text-3xl text-ink-900 leading-tight">Welcome back</p>
            <p className="text-sm text-ink-500 mt-2">Just your phone number. We&apos;ll text you a code.</p>
            <div className="mt-6">
              <label className="text-[12px] tracking-wide uppercase text-ink-500">Your phone (with country code)</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+919876543210"
                data-testid="signin-phone-input"
                inputMode="tel"
                className="mt-1 w-full h-[56px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-base outline-none focus:border-sage-500"/>
            </div>
            {err && <p className="mt-3 text-sm text-brick-500" data-testid="signin-error">{err}</p>}
            <button onClick={submitSignInPhone} disabled={busy} data-testid="signin-send-otp"
              className="mt-6 w-full h-[56px] rounded-full bg-ink-900 text-white font-semibold disabled:opacity-60">
              {busy ? "Sending…" : "Send OTP"}
            </button>
            <p className="text-[11px] text-ink-500 text-center mt-4">
              Not registered yet? <button onClick={() => { setStep("welcome"); setMode("signup"); }} className="underline text-ink-700">Create an account</button>.
            </p>
          </motion.div>
        )}

        {step === "emailOtp" && (
          <motion.div key="emailOtp" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1 flex flex-col" data-testid="email-otp-step">
            <button onClick={() => setStep("phone")} className="text-ink-500 text-sm mb-4 self-start" data-testid="email-otp-back">← Back</button>
            <p className="font-display text-3xl text-ink-900 leading-tight">Check your email</p>
            <p className="text-sm text-ink-500 mt-2">
              We sent a 6-digit code to <span className="font-semibold text-ink-900">{email}</span>. This one-time step confirms it&apos;s really you - nothing else.
            </p>

            {emailHint && (
              <div className="mt-4 bg-terracotta-50 border border-terracotta-400/30 rounded-2xl px-4 py-3" data-testid="email-otp-hint">
                <p className="text-[12px] text-terracotta-600 font-semibold">DEMO MODE</p>
                <p className="text-[13px] text-ink-900 mt-1">{emailHint}</p>
              </div>
            )}

            <div className="mt-8">
              <input value={emailOtp} onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="••••••" inputMode="numeric" autoFocus maxLength={6}
                data-testid="email-otp-input"
                className="w-full h-[72px] px-5 rounded-2xl border-2 border-sand-200 bg-white text-3xl tracking-[0.5em] text-center font-display outline-none focus:border-sage-500"/>
            </div>

            <button onClick={onResendEmailOtp} disabled={busy} data-testid="email-otp-resend"
              className="mt-4 text-[13px] text-sage-700 font-medium self-start">
              Didn&apos;t receive it? Resend
            </button>

            {err && <p className="mt-4 text-sm text-brick-500" data-testid="email-otp-error">{err}</p>}

            <div className="mt-auto pt-6">
              <button onClick={onVerifyEmailOtp} disabled={busy || emailOtp.length !== 6} data-testid="email-otp-verify"
                className="w-full h-[60px] rounded-full bg-sage-500 hover:bg-sage-600 text-white text-base font-semibold disabled:opacity-50 transition">
                {busy ? "Verifying…" : "Verify email → send phone code"}
              </button>
              <p className="mt-3 text-[12px] text-ink-500 text-center leading-relaxed">
                We&apos;ll never send you marketing email. Only sign-in codes and account recovery.
              </p>
              <IndoBlitzFooter/>
            </div>
          </motion.div>
        )}

        {step === "otp" && (
          <motion.div key="otp" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex-1 flex flex-col">
            <button onClick={() => setStep("phone")} className="text-ink-500 text-sm mb-4 self-start" data-testid="otp-back">← Back</button>
            <p className="font-display text-3xl text-ink-900 leading-tight">Enter the code</p>
            <p className="text-sm text-ink-500 mt-2">
              Sent via{" "}
              <span className="font-semibold text-ink-900">
                {sentChannel === "whatsapp" ? "WhatsApp" : sentChannel === "sms" ? "SMS" : "demo channel"}
              </span>
              {" "}to <span className="font-semibold text-ink-900">{phone}</span>
            </p>

            {hint && (
              <div className="mt-4 bg-terracotta-50 border border-terracotta-400/30 rounded-2xl px-4 py-3" data-testid="otp-hint">
                <p className="text-[12px] text-terracotta-600 font-semibold">DEMO MODE</p>
                <p className="text-[13px] text-ink-900 mt-1">{hint}</p>
              </div>
            )}

            <div className="mt-8">
              <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="••••••" inputMode="numeric" autoFocus maxLength={6}
                data-testid="otp-input"
                className="w-full h-[72px] px-5 rounded-2xl border-2 border-sand-200 bg-white text-3xl tracking-[0.5em] text-center font-display outline-none focus:border-sage-500"/>
            </div>

            {/* WhatsApp → SMS fallback */}
            {sentChannel === "whatsapp" && (
              <div className="mt-4 flex items-center justify-center">
                {smsCountdown > 0 ? (
                  <p className="text-[12px] text-ink-500" data-testid="sms-countdown">
                    No WhatsApp? You can resend via SMS in <span className="font-semibold text-ink-700">{smsCountdown}s</span>
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={onResendViaSms}
                    disabled={smsResending}
                    data-testid="resend-via-sms"
                    className="text-[13px] font-semibold text-terracotta-600 underline-offset-4 hover:underline disabled:opacity-50"
                  >
                    {smsResending ? "Sending via SMS…" : "Didn't get it? Send via SMS →"}
                  </button>
                )}
              </div>
            )}

            {sentChannel === "sms" && (
              <p className="mt-4 text-center text-[12px] text-ink-500" data-testid="sms-sent-confirmation">
                Sent via SMS. Check your text messages.
              </p>
            )}

            {err && <p className="mt-4 text-sm text-brick-500" data-testid="otp-error">{err}</p>}

            {/* Countdown + resend. Prevents endpoint hammering, gives users a
                clear "Aarya is still trying" reassurance. */}
            <div className="mt-4 text-center" data-testid="otp-resend-block">
              {otpCountdown > 0 ? (
                <p className="text-[12px] text-ink-500" data-testid="otp-countdown">
                  Resend available in <span className="font-semibold text-ink-900">{otpCountdown}s</span>
                </p>
              ) : (
                <button
                  onClick={onResendViaSms}
                  disabled={smsResending}
                  data-testid="otp-resend-btn"
                  className="text-[13px] font-semibold text-terracotta-600 underline underline-offset-4 hover:opacity-80 disabled:opacity-50"
                >
                  {smsResending ? "Resending…" : "Resend OTP"}
                </button>
              )}
            </div>

            <div className="mt-auto pt-8">
              <button onClick={onVerify} disabled={busy || otp.length !== 6} data-testid="verify-otp-button"
                className="w-full h-[60px] rounded-full bg-sage-500 hover:bg-sage-600 text-white text-base font-semibold disabled:opacity-50 transition">
                {busy ? "Verifying…" : "Verify & Enter"}
              </button>
              <p className="text-center text-[11px] text-ink-500 mt-3">
                An <span className="font-semibold text-ink-900">IndoBlitz</span> product.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <TermsSheet open={termsOpen} onClose={() => setTermsOpen(false)}/>
      <TrustSheet open={trustOpen} onClose={() => setTrustOpen(false)}/>
    </div>
  );
}
